<template>
  <div class="prozess-wrapper">
    <div class="prozess">
      <div>
        <div v-if="!claimed">
          <TaskBlocker :assignee="data.assignee" class="mb-6" />
        </div>
        <div v-if="!isLoading">
          <v-row class="mb-4 ml-1">
            <v-col cols="7">
              <ProjectDetails
                :project="projectData"
                hideCleverReachButton
                hideBrevoButton
                hideTkiButton
                hideStatistic
              ></ProjectDetails>
            </v-col>
            <v-col cols="3" class="ml-4">
              <data-display label="Änderungsdatum" class="mb-4">
                <template v-if="nextScheduledJob !== null">
                  {{ dueDate }}
                  <v-icon
                    :disabled="!claimed"
                    class="mx-2"
                    title="Ausführung verschieben"
                    style="cursor: pointer"
                    @click.stop="modifyDueDateDialog = true"
                  >
                    {{ icons.mdiTimerEditOutline }}
                  </v-icon>
                  <span v-if="dueDate !== newDueDate">
                    Neuer Wert:
                    <span class="bold indigo--text text--lighten-1">{{
                      newDueDate
                    }}</span>
                  </span>
                </template>
                <template v-else> - </template>
              </data-display>
              <data-display v-if="isDistributionEditAllowed" label="Vertriebsphase" class="mb-4">
                <v-autocomplete
                  v-model="updateDistributionStatus"
                  :items="distributionStatus"
                  :disabled="!claimed"
                  item-text="description"
                  item-value="name"
                >
                  <template v-slot:selection="{ item }">
                    <span :style="{ color: item.color }">{{
                      item.description
                    }}</span>
                  </template>
                  <template v-slot:item="{ item }">
                    <span :style="{ color: item.color }">{{
                      item.description
                    }}</span>
                  </template>
                </v-autocomplete>
              </data-display>
              <data-display v-if="isTariffModelEditAllowed" label="Tarifmodell" class="mb-4">
                <v-autocomplete
                  v-model="updateTariffModels"
                  :items="tariffModels"
                  :disabled="!claimed"
                  item-text="name"
                  item-value="tariffId"
                >
                  <template v-slot:item="{ item }">
                    <span>{{
                      item.tariffId === NO_MODIFY ? '-' : item.tariffId
                    }}</span>
                    <span style="position: absolute; left: 75px">{{
                      item.name
                    }}</span>
                  </template>
                </v-autocomplete>
              </data-display>
            </v-col>
          </v-row>
          <MemoField v-model="memo" :disabled="!claimed" :counter="60" />
        </div>
        <div v-else class="my-8">
          <span>Daten werden geladen</span>
          <v-progress-linear
            color="blue accent-4"
            indeterminate
            rounded
            height="6"
          ></v-progress-linear>
        </div>
        <v-card-actions v-if="!isLoading">
          <v-spacer></v-spacer>
          <v-btn
            class="mx-6"
            color="secondary"
            :disabled="!claimed || !memoIsValid"
            @click="saveMemo()"
          >
            Memo Speichern
          </v-btn>

          <v-btn
            class="mx-6"
            color="warning"
            :disabled="!claimed"
            @click="abortChangesModal = true"
          >
            Prozess beenden
          </v-btn>

          <v-btn
            class="ml-6"
            color="primary"
            :disabled="!claimed || !formHasChanged"
            @click="reviewChangesModal = true"
          >
            Prozess updaten
          </v-btn>
        </v-card-actions>
      </div>
      <v-dialog
        v-model="modifyDueDateDialog"
        :persistent="isModifying"
        width="700px"
      >
        <v-card>
          <v-card-text class="pt-6">
            <v-container>
              <v-row>
                <v-col cols="6">
                  <v-date-picker
                    v-model="newDueDateDate"
                    class="mt-4"
                    :min="new Date().toISOString().split('T')[0]"
                  ></v-date-picker>
                </v-col>
                <v-col cols="6">
                  <v-time-picker v-model="newDueDateTime" format="24hr" />
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              color="primary"
              @click="modifyDueDateDialog = !modifyDueDateDialog"
            >
              Ok
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog
        v-model="abortChangesModal"
        :persistent="isModifying"
        width="700px"
      >
        <v-card>
          <v-card-text class="pt-6">
            <v-container>
              <v-row>
                <v-col cols="12">
                  <p>Soll der Prozess wirklich abgebrochen werden?</p>
                  <p>Dadurch wird keine der Änderungen mehr ausgeführt!</p>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-btn
              color="primary"
              :disabled="isModifying"
              @click="abortChangesModal = !abortChangesModal"
            >
              Abbrechen
            </v-btn>
            <v-spacer />
            <v-btn color="warning" :loading="isModifying" @click="abortProcess">
              Ja, Prozess abbrechen
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="reviewChangesModal" :persistent="true" width="700px">
        <v-card>
          <v-card-text class="pt-6">
            <v-container>
              <v-row>
                <v-col cols="12">
                  <v-simple-table>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-left"></th>
                          <th class="text-left">Alter Wert</th>
                          <th class="text-left">Neuer Wert</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="hasDateChanged">
                          <td>Ausführung</td>
                          <td>{{ dueDate }}</td>
                          <td>{{ newDueDate }}</td>
                        </tr>
                        <tr v-if="hasDistributionStatusChanged">
                          <td>Vertriebsphase</td>
                          <td>
                            {{
                              getDistributionStatusByName(
                                currentDistributionStatus
                              ).description
                            }}
                          </td>
                          <td>
                            {{
                              getDistributionStatusByName(
                                updateDistributionStatus
                              ).description
                            }}
                          </td>
                        </tr>
                        <tr v-if="hasTariffModelChanged">
                          <td>Tarifmodell</td>
                          <td>
                            {{
                              getTariffModelByTariffId(currentTariffModel).name
                            }}
                          </td>
                          <td>
                            {{
                              getTariffModelByTariffId(updateTariffModels).name
                            }}
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-btn
              color="secondary"
              @click="reviewChangesModal = !reviewChangesModal"
            >
              Abbrechen
            </v-btn>
            <v-spacer />
            <v-btn color="primary" @click="submitChanges()">
              Ja, Prozess updaten
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>
<script>
import { HTTP } from '@/main/httpClient.js';
import NotificationObject from '@/main/NotificationObject.js';
import { mdiTimerEditOutline } from '@mdi/js';
import { adminGroups } from '@/main/routes';

const TIMEZONE = 'Europe/Berlin';
const NO_MODIFY = 'no-modify';

export default {
  name: 'ActionProjektAnpassungTask',
  components: {
    TaskBlocker: () => import('@/task/TaskBlocker'),
    DisplayContainer: () => import('@/components/elements/DisplayContainer'),
    DataDisplay: () => import('@/components/elements/DataDisplay.vue'),
    MemoField: () => import('@/components/elements/MemoField.vue'),
    ProjectDetails: () =>
      import('@/components/constructionProjects/ProjectDetails.vue')
  },
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  data: () => ({
    icons: {
      mdiTimerEditOutline
    },
    NO_MODIFY,
    taskId: null,
    isLoading: true,
    isModifying: false,
    reviewChangesModal: false,
    modifyDueDateDialog: false,
    newDueDateTime: null,
    newDueDateDate: null,
    nextScheduledJob: null,
    projectData: null,
    projectId: null,
    modifyDto: null,
    tariffModels: [],
    updateTariffModels: null,
    distributionStatus: [],
    updateDistributionStatus: null,
    memo: '',
    abortChangesModal: false
  }),
  computed: {
    username() {
      return this.$store.getters.getUsername;
    },
    userGroups: function () {
      return this.$auth.user().userRoles;
    },
    isAdmin: function () {
      return this.userHasAnyGroup(adminGroups);
    },
    isDistributionEditAllowed: function () {
      return this.isAdmin || this.userHasAnyGroup('LgSpotEcommerce');
    },
    isTariffModelEditAllowed: function () {
      return this.isAdmin || this.userHasAnyGroup('LgSpotEcommerce');
    },
    isEditAllowed: function () {
      return (
        this.isAdmin ||
        this.isDistributionEditAllowed ||
        this.isTariffModelEditAllowed
      );
    },
    claimed() {
      return this.data.assignee === this.username;
    },
    camundaAdmin() {
      return this.$auth.user().userRoles.includes('camunda-admin');
    },
    memoIsValid() {
      return this.memo == null || this.memo.length <= 60;
    },
    dueDateObject() {
      if (this.nextScheduledJob === null) return null;

      const nextDueDate = new Date(this.nextScheduledJob.dueDate);

      return nextDueDate;
    },
    dueDate() {
      if (this.dueDateObject === null) return null;

      return this.dueDateObject.toLocaleString('de-DE', {
        timeZone: TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    newDueDate() {
      if (!this.newDueDateDate || !this.newDueDateTime) return null;

      const newDueDate = new Date(
        this.newDueDateDate + ' ' + this.newDueDateTime
      );

      return newDueDate.toLocaleString('de-DE', {
        timeZone: TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    currentDistributionStatus() {
      return (
        this.distributionStatus.find(
          (d) => d.name === this.modifyDto?.restoreDistributionStatusName
        )?.name ?? NO_MODIFY
      );
    },
    currentTariffModel() {
      return (
        this.tariffModels.find(
          (t) => t.name === this.modifyDto?.restoreTariffModelName
        )?.tariffId ?? NO_MODIFY
      );
    },
    hasDateChanged() {
      return this.dueDate !== this.newDueDate;
    },
    hasDistributionStatusChanged() {
      return this.currentDistributionStatus !== this.updateDistributionStatus;
    },
    hasTariffModelChanged() {
      return this.currentTariffModel !== this.updateTariffModels;
    },
    formHasChanged() {
      return (
        this.updateTariffModels !== this.updateDistributionStatus &&
        (this.hasDateChanged ||
          this.hasDistributionStatusChanged ||
          this.hasTariffModelChanged)
      );
    }
  },
  watch: {
    data: {
      deep: true,
      immediate: true,
      handler: async function (newVal) {
        if (this.taskId === null || this.taskId !== newVal.id) {
          this.taskId = newVal.id;
          this.isLoading = true;
          await new Promise((resolve) => setTimeout(resolve, 1));

          await Promise.all([
            this.loadTaskVariables(),
            this.loadProcessJobs(),
            this.loadTariffModels(),
            this.loadDistributionStatus()
          ]);

          await Promise.all([this.loadProject(), this.handleSelects()]);

          this.isLoading = false;
        }
      }
    }
  },
  methods: {
    loadTaskVariables: function () {
      return HTTP.get(`/task/${this.data.id}/variables`)
        .then((response) => {
          if (response.data.memo !== undefined) {
            this.memo = response.data.memo.value;
          }

          this.projectId = response.data.projectId.value;
          this.modifyDto = response.data.updateProjectParameter.value;
        })
        .catch((response) => {
          this.showToast(
            'error',
            `Fehler beim laden der Task Variablen HTTP Status Code ${response.status}`
          );
        });
    },
    loadProcessJobs: function () {
      HTTP.post('job', {
        processInstanceId: this.data.processInstanceId,
        dueDates: [
          {
            operator: 'gt',
            value: this.toCamundaDate(new Date())
          }
        ]
      })
        .then((res) => {
          if (res.data.length !== 1) {
            throw new Error('Fehler beim laden der Jobs');
          }

          this.nextScheduledJob = res.data[0];

          const [date] = this.dueDateObject.toISOString().split('T');
          const time = this.dueDateObject.toLocaleTimeString('de-DE', {
            timeZone: TIMEZONE
          });
          this.newDueDateDate = date;
          this.newDueDateTime = time.split('.')[0];
        })
        .catch(() => {
          this.showToast(
            'error',
            'Die Prozessdaten konnten nicht geladen werden!'
          );
        });
    },
    loadProject: function () {
      return HTTP.get(
        '/past/projects?where[projektId][equals]=' + this.projectId
      )
        .then((response) => {
          this.projectData = response.data[0];
        })
        .catch((error) => console.log(error));
    },
    loadTariffModels: function () {
      return HTTP.get('past/tariffModels').then((res) => {
        this.tariffModels = res.data;
      });
    },
    loadDistributionStatus: function () {
      return HTTP.get('past/distributionStatuses').then((res) => {
        this.distributionStatus = res.data;
      });
    },
    submitChanges: function () {
      this.isModifying = true;
      const updateDto = {};

      if (this.hasDateChanged) {
        updateDto.restoreDate = this.toJavaDate(
          new Date(this.newDueDateDate + ' ' + this.newDueDateTime)
        );
      }

      if (this.isDistributionEditAllowed && this.hasDistributionStatusChanged) {
        updateDto.restoreDistributionStatusName = this.updateDistributionStatus;
      }

      if (this.isTariffModelEditAllowed && this.hasTariffModelChanged) {
        updateDto.restoreTariffModelName = this.getTariffModelByTariffId(
          this.updateTariffModels
        ).name;
      }

      HTTP.post(
        `projectConstructionState/project-update/${this.taskId}/update`,
        updateDto
      )
        .then(() => {
          this.isModifying = false;
          this.reviewChangesModal = false;
          this.reloadTaskData();
        })
        .catch(() => {
          this.showToast(
            'error',
            'Die Ausführung konnte nicht verschoben werden!'
          );
        });
    },
    abortProcess: function () {
      this.isModifying = true;
      HTTP.delete(
        `projectConstructionState/project-update/${this.taskId}/cancel`
      )
        .then(() => {
          this.showToast(
            'success',
            'Der Prozess wurde erfolgreich abgebrochen!'
          );

          this.isModifying = false;
          this.$emit('onClose');
        })
        .catch(() => {
          this.showToast(
            'error',
            'Der Prozess konnte nicht abgebrochen werden!'
          );
        });
    },
    reloadTaskData: async function () {
      this.isLoading = true;

      await new Promise((resolve) => setTimeout(resolve, 1));
      await Promise.all([this.loadTaskVariables(), this.loadProcessJobs()]);
      await this.handleSelects();

      this.isLoading = false;
    },
    saveMemo: function () {
      HTTP.post(`/task/${this.data.id}/localVariables`, {
        modifications: {
          memo: {
            value: this.memo,
            type: 'String'
          }
        }
      }).then(() => {
        this.showToast('success', 'Memo gespeichert');
      });
    },
    handleSelects: function () {
      this.tariffModels = [
        {
          name: 'Nicht ändern',
          tariffId: NO_MODIFY
        },
        ...this.tariffModels
      ];

      this.distributionStatus = [
        {
          color: null,
          description: 'Nicht ändern',
          name: NO_MODIFY
        },
        ...this.distributionStatus
      ];

      this.updateDistributionStatus = this.currentDistributionStatus;
      this.updateTariffModels = this.currentTariffModel;
    },
    showToast: function (type, message) {
      this.$store.commit(
        'addNotification',
        new NotificationObject(type, message)
      );
    },
    userHasAnyGroup(groupsToCheck) {
      if (!Array.isArray(groupsToCheck)) {
        groupsToCheck = [groupsToCheck];
      }

      return groupsToCheck.some((g) => this.userGroups.includes(g));
    },
    parseDate: function (date) {
      return new Date(Date.parse(date));
    },
    convertDate: function (date) {
      if (!date) return '-';
      return this.parseDate(date).toLocaleDateString('de-DE');
    },
    getDistributionStatusByName(name) {
      return this.distributionStatus.find((d) => d.name === name);
    },
    getTariffModelByTariffId(id) {
      return this.tariffModels.find((t) => t.tariffId === id);
    },
    toCamundaDate(date) {
      const iso = date.toISOString();
      const offset = -date.getTimezoneOffset();
      const sign = offset >= 0 ? '+' : '-';
      const absOffset = Math.abs(offset);
      const offsetStr = `${sign}${String(Math.floor(absOffset / 60)).padStart(
        2,
        '0'
      )}${String(absOffset % 60).padStart(2, '0')}`;
      return iso.replace('Z', offsetStr);
    },
    toJavaDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');

      const offset = -date.getTimezoneOffset();
      const sign = offset >= 0 ? '+' : '-';
      const absOffset = Math.abs(offset);

      const offsetStr =
        `${sign}${String(Math.floor(absOffset / 60)).padStart(2, '0')}:` +
        `${String(absOffset % 60).padStart(2, '0')}`;

      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${offsetStr}`;
    }
  }
};
</script>

<style scoped>
.details {
  display: grid;
  grid-template-columns: calc(50% - var(--goe-spacing-0_5)) calc(
      50% - var(--goe-spacing-0_5)
    );
  grid-gap: var(--goe-spacing-1);
}

.dialog-buttons :first-child {
  margin-right: 5px;
}
</style>
